import { fetchDriverManifest, stampDriverStopRemote } from "@/lib/logistics/driverClient";
import type { DriverManifest, PendingDriverStamp } from "@/lib/logistics/driverTypes";
import {
  applyStampToManifest,
  enqueuePendingDriverStamp,
  listPendingDriverStamps,
  readCachedDriverManifest,
  removePendingDriverStamp,
  writeCachedDriverManifest,
} from "@/lib/driver/driverCache";

export type DriverSyncResult = {
  manifest: DriverManifest | null;
  conflicts: string[];
  error: string | null;
  fromCache: boolean;
};

export async function loadDriverManifestWithCache(): Promise<DriverSyncResult> {
  const cached = await readCachedDriverManifest();
  if (!navigator.onLine) {
    return { manifest: cached, conflicts: [], error: null, fromCache: true };
  }

  const remote = await fetchDriverManifest();
  if (!remote.ok) {
    return {
      manifest: cached,
      conflicts: [],
      error: remote.error,
      fromCache: !!cached,
    };
  }

  const manifest = remote.data.manifest;
  await writeCachedDriverManifest(manifest);
  const conflicts = await flushPendingDriverStamps(manifest);
  const refreshed = conflicts.length ? await fetchDriverManifest() : null;
  if (refreshed?.ok) {
    await writeCachedDriverManifest(refreshed.data.manifest);
    return {
      manifest: refreshed.data.manifest,
      conflicts,
      error: null,
      fromCache: false,
    };
  }
  return { manifest, conflicts, error: null, fromCache: false };
}

async function flushPendingDriverStamps(
  serverManifest: DriverManifest
): Promise<string[]> {
  const pending = await listPendingDriverStamps();
  const conflicts: string[] = [];
  const serverStopById = new Map<string, { arrived_at: string | null; departed_at: string | null }>();
  for (const route of serverManifest.routes) {
    for (const stop of route.stops) {
      serverStopById.set(stop.id, { arrived_at: stop.arrived_at, departed_at: stop.departed_at });
    }
  }

  for (const item of pending) {
    const server = serverStopById.get(item.stop_id);
    const serverVal = server?.[item.field] ?? null;
    if (serverVal && serverVal !== item.value) {
      conflicts.push(item.stop_id);
      await removePendingDriverStamp(item.id);
      continue;
    }

    const res = await stampDriverStopRemote({
      stop_id: item.stop_id,
      field: item.field,
      value: item.value,
    });
    if (res.ok) {
      await removePendingDriverStamp(item.id);
      continue;
    }
    if (res.status === 403 || res.status === 404) {
      conflicts.push(item.stop_id);
      await removePendingDriverStamp(item.id);
    }
  }
  return conflicts;
}

export async function stampDriverStopLocal(opts: {
  manifest: DriverManifest;
  stopId: string;
  field: PendingDriverStamp["field"];
}): Promise<{ manifest: DriverManifest; conflicts: string[]; error: string | null }> {
  const value = new Date().toISOString();
  const optimistic = applyStampToManifest(opts.manifest, opts.stopId, opts.field, value);
  await writeCachedDriverManifest(optimistic);
  const pending = await enqueuePendingDriverStamp({
    stop_id: opts.stopId,
    field: opts.field,
    value,
  });

  if (!navigator.onLine) {
    return { manifest: optimistic, conflicts: [], error: null };
  }

  const res = await stampDriverStopRemote({
    stop_id: opts.stopId,
    field: opts.field,
    value,
  });
  if (!res.ok) {
    return { manifest: optimistic, conflicts: [], error: res.error };
  }

  await removePendingDriverStamp(pending.id);
  const sync = await loadDriverManifestWithCache();
  return {
    manifest: sync.manifest ?? optimistic,
    conflicts: sync.conflicts,
    error: sync.error,
  };
}
